import React, { useRef, useState, useEffect, useCallback } from "react";
import { useUsuario } from '../../context/usuarioContext';
import { useNavigate } from 'react-router-dom';
// Componentes UI
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { InputNumber } from "primereact/inputnumber";
// Datos
import { obtenerFlota } from '../../services/vehiculosService';
import { agregarCklVehiculo, notificarAlerta, obtenerCklVehiculo } from "../../services/conductoresService";
// Hooks y Validaciones
import { useForm, Controller, FormProvider, useFormContext, useController } from "react-hook-form";
// Utils
import { generarPDF } from "../../utils/pdf/pdfCklVehiculo";
import { formatFecha } from "../../utils/dateFormatter";
import ModuleLayout from '../../layouts/layout-modulo.js';
// Estilos y Assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import "../../pages/panel-conductor/ckl-vehiculo.css";

const PATENTES_ADBLUE = [ "RZFS71", "RZFS72", "SHCZ32", "SVSG34", "THKR85", "TPCH10", "TSKR31", "TVCX20", "VDBG96", "VDJZ25", "VDJZ35", "VGVZ81", "VHPW67", "VSWP68" ]

export default function CklVehiculo() {
  const toast = useRef(null);
  const navigate = useNavigate();
  const btnVolverRef = useRef(null);
  const { usuario } = useUsuario();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehiculosByPatente, setVehiculosByPatente] = useState({});
  const [patentes, setPatentes] = useState([]);
  
  const buildDefaultValues = (usuario) => ({
    conductor: `${usuario?.nombre || ""} ${usuario?.apellido_paterno || ""}`.trim(),
    id_usuario: usuario?.id_usuario || "",
    patente: "",
    id_vehiculo: "",
    modelo: "",
    tipoVehiculo: "",
    kmActual: "",
    kmBase: 0,
    vencExtintor: "",
    vencDecreto80: "",
    vencRevisionTecnica: "",
    vencPermisoCirculacion: "",
    vencLaminaSeguridad: "",
    vencSoap: "",
    observaciones: "",
  });

  const methods = useForm({
    defaultValues: buildDefaultValues(usuario),
    mode: 'onBlur',
    shouldUnregister: true,
  });

  const {
    handleSubmit, control, reset, watch, getValues, setValue,
  } = methods;

  // Obtener vehículos registrados 
  const getVehiculos = useCallback(async () => {
    try {
      const res = await obtenerFlota();

      if (!Array.isArray(res)) throw new Error("Respuesta inesperada al obtener vehiculos");

      const map = {};

      res.forEach(v => {
        const patente = String(v.patente || "").toUpperCase().trim();
        if (!patente) return;
        map[patente] = { ...v, patente };
      });

      // Información del vehículo, accesible por patente
      setVehiculosByPatente(map);

      // Listado de patentes
      setPatentes(Object.keys(map));
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los vehículos',
        sticky: true
      });
    }
  }, []);
  
  useEffect(() => { getVehiculos(); }, [getVehiculos]);

  const prellenarDesdeVehiculo = (patente) => {
    const normalizada = (patente || "").toUpperCase().trim();
    const v = vehiculosByPatente[normalizada];

    if (!v) {
      // Si la patente no existe en la BD
      setValue("patente", normalizada, { shouldDirty: true });
      setValue("id_vehiculo", "", { shouldDirty: true });
      setValue("modelo", "", { shouldDirty: true });
      setValue("tipoVehiculo", "", { shouldDirty: true });
      return;
    }

    setValue("patente", normalizada, { shouldDirty: true });
    setValue("id_vehiculo", v.id_vehiculo || "", { shouldDirty: true });
    setValue("modelo", v.modelo || "", { shouldDirty: true });
    setValue("tipoVehiculo", v.tipo || "", { shouldDirty: true });
    setValue("kmActual", v.kmActual ?? "", { shouldDirty: true });
    setValue("kmBase", v.kmActual ?? 0);
    setValue("vencExtintor", v.vencExtintor?.slice(0,10) || "", { shouldDirty: true });
    setValue("vencDecreto80", v.vencDecreto80?.slice(0,10) || "", { shouldDirty: true });
    setValue("vencRevisionTecnica", v.vencRevisionTecnica?.slice(0,10) || "", { shouldDirty: true });
    setValue("vencPermisoCirculacion", v.vencPermisoCirculacion?.slice(0,10) || "", { shouldDirty: true });
    setValue("vencLaminaSeguridad", v.vencLaminaSeguridad?.slice(0,10) || "", { shouldDirty: true });
    setValue("vencSoap", v.vencSoap?.slice(0,10) || "", { shouldDirty: true });
  };

  const tipoVehiculo = watch("tipoVehiculo");
  const modeloVehiculo = watch("modelo");
  const patenteSeleccionada = watch("patente");

  // Marca todos los campos Bueno/Revisar/Falta con "bueno"
  const quickFillBueno = () => {
    const tipo = (getValues("tipoVehiculo") || "").toUpperCase();
    const patente = getValues("patente");

    DEFINICION_CHECKLIST.forEach(({ pregunta, tipo: tipoCampo, aplica }) => {
      if (tipoCampo === "sino") return; // solo estado

      const aplicaCampo = typeof aplica === "function" ? aplica(tipo, patente) : true;
      if (!aplicaCampo) return;

      setValue(pregunta, "bueno", { shouldDirty: true });
    });
  };

  // Marca todos los campos Sí/No con "si"
  const quickFillSi = () => {
    const tipo = (getValues("tipoVehiculo") || "").toUpperCase();
    DEFINICION_CHECKLIST.forEach(({ pregunta, tipo: tipoCampo, aplica }) => {
      if (tipoCampo !== "sino") return; // solo si/no
      const aplicaCampo = typeof aplica === "function" ? aplica(tipo) : true;
      if (!aplicaCampo) return;
      setValue(pregunta, "si", { shouldDirty: true });
    });
  };

  const limpiarForm = () => reset(buildDefaultValues(usuario));
  
  const enviarChecklist = async (data) => {
    setIsSubmitting(true);

    const tipo = (data.tipoVehiculo || "").toUpperCase();

    // Arreglo para enviar
    const cabecera = {
      id_usuario: data.id_usuario,
      conductor: data.conductor,
      patente: data.patente,
      id_vehiculo: data.id_vehiculo,
      tipoVehiculo: data.tipoVehiculo,
      kmActual: data.kmActual,
      observaciones: data.observaciones || "",
      vencExtintor: data.vencExtintor || null,
      vencDecreto80: data.vencDecreto80 || null,
      vencRevisionTecnica: data.vencRevisionTecnica || null,
      vencPermisoCirculacion: data.vencPermisoCirculacion || null,
      vencLaminaSeguridad: data.vencLaminaSeguridad || null,
      vencSoap: data.vencSoap || null,
    };

    // Detalle
    const items = DEFINICION_CHECKLIST
      .map(({ pregunta, grupo, aplica }) => {
        const aplicaEsteCampo = typeof aplica === "function" ? aplica(tipo) : true;
        if (!aplicaEsteCampo) return null;

        const respuesta = data[pregunta];
        if (!respuesta) return null;

        return { grupo, pregunta, respuesta };
      })
      .filter(Boolean);
    
    const payload = { cabecera, detalle: items };

    try {
      const resp = await agregarCklVehiculo(payload);

      if (!resp?.ok) throw new Error('No se pudo registrar el checklist');

      toast.current?.show({
        severity: 'success', 
        summary: 'Checklist enviado', 
        detail: 'Se registró su evaluación correctamente.',
        sticky: true
      });

      // --------------- Notificaciones -------------
      const nivelAlerta = resp?.estado_alerta?.det_estado;
      const tipoChecklist = resp?.tipoChecklist;
      const cod_checklist = resp?.cod_checklist;
      const id_checklist = resp?.id_checklist;
      
      let estado_documentacion;

      if (resp?.estado_alerta) {
        if (resp.estado_alerta?.porVencer) {
          estado_documentacion = "Documentación por vencer";
        } else if (resp.estado_alerta?.vencido) {
          estado_documentacion = "Documentación vencida";
        } else {
          estado_documentacion = "Documentación vigente";
        }
      }

      // UI de alertas
      mostrarAlertasUI(nivelAlerta);

      if (nivelAlerta !== 'OK') {
        try {
          await notificarAlertaChecklist({ id_checklist, cod_checklist, nivelAlerta, tipoChecklist, estado_documentacion });
          toast.current?.show({
            severity: 'info',
            summary: 'Notificación enviada',
            detail: 'Se ha notificado a la supervisión.',
            sticky: true
          });
        } catch {
          toast.current?.show({
            severity: 'warn',
            summary: 'Checklist registrado',
            detail: 'No se pudo notificar al supervisor por correo.',
            sticky: true
          });
        }
      }

      // Limpia el formulario
      limpiarForm();
      // Enfoca al botón volver
      btnVolverRef.current?.focus?.(); 
    
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al enviar',
        detail: error || 'Error del servidor.',
        life: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const notificarAlertaChecklist = async ({ id_checklist, cod_checklist, nivelAlerta, tipoChecklist, estado_documentacion }) => {
    try {
      // Obtener datos estructurados del checklist
      const data = await obtenerCklVehiculo(id_checklist);
      
      if (!data) throw new Error('No se recibieron datos');
      
      // Generar Blob(PDF) a partir de la data estructurada
      const pdf = await new Promise((resolve) => {
        generarPDF(data).getBlob((blob) => { resolve(blob); });
      });

      // Adjuntar datos para el correo
      const formData = new FormData();
      formData.append('checklist', pdf, `checklist_${cod_checklist}.pdf`);
      formData.append('nivelAlerta', nivelAlerta);
      formData.append('tipoChecklist', tipoChecklist);
      formData.append('conductor', data.cabecera.conductor);
      formData.append('patente', data.cabecera.patente);
      formData.append('estadoDocumentacion', estado_documentacion);

      // Enviar correo
      const resp = await notificarAlerta(cod_checklist, formData);

      return resp;

    } catch (error) {
      throw error;
    }
  };

  const mostrarAlertasUI = (nivelAlerta) => {
    if (nivelAlerta === 'OK') {
      toast.current?.show({
        severity: 'success',
        summary: 'Vehículo en condiciones operativas',
        detail: 'El vehículo se encuentra en condiciones para operar. ¡Que tenga buen viaje!',
        sticky: true
      });
    }

    if (nivelAlerta === 'ALERTA_CRITICA') {
      toast.current?.show({
        severity: 'error',
        summary: 'Alerta crítica de vehículo',
        detail: 'Se detectaron fallas importantes en el vehículo. No se recomienda su uso hasta ser revisado en taller.',
        sticky: true
      });
      toast.current?.show({
        severity: 'info',
        summary: 'Instrucciones',
        detail: 'Por favor, contacte a su supervisor o al área de prevención para recibir instrucciones adicionales.',
        sticky: true
      });
    }

    if (nivelAlerta === 'ALERTA_MEDIA') {
      toast.current?.show({
        severity: 'warn',
        summary: 'Alerta moderada de vehículo',
        detail: 'Se detectaron observaciones que requieren revisión.',
        sticky: true
      });
      toast.current?.show({
        severity: 'info',
        summary: 'Recomendación',
        detail: 'Se recomienda agendar una cita con el taller para evaluar el vehículo antes de que las observaciones evolucionen a una falla crítica.',
        sticky: true
      });
    }
  };

  return (
    <ModuleLayout titulo="Panel del Conductor" logo={logo} onVolver={() => navigate('/app/panel-conductor')}>
      <Toast ref={toast} position='center'/>
      <FormProvider {...methods}>
        <section className="tmc-content mb-4">
          <h3 className='titulo-form'>Checklist de Vehículo</h3>
          <section className="form-section">
            <form onSubmit={handleSubmit(enviarChecklist)} className="container-form">
              {/* Datos generales */}
              <Section title="I. DATOS GENERALES">
                {/* CONDUCTOR */}
                <div className="form-comprobante-field mb-2">
                  <InputRow name="conductor" label="Conductor" disabled />
                  <input type="hidden" {...methods.register("id_usuario")} />
                </div>
                {/* PATENTE */}
                <div className="form-comprobante-field mb-2">
                  <label className="form-label">Patente <span className="required">*</span></label>
                  <Controller
                    name="patente"
                    control={control}
                    rules={{ 
                      required: "Debe seleccionar una patente",
                      validate: (value) => {
                        const patenteNormalizada = (value || "").toUpperCase().trim();
                        if (!patenteNormalizada) return "Debe seleccionar una patente";
                        return patentes.includes(patenteNormalizada) || "La patente ingresada no corresponde a ningún vehículo registrado.";
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <input
                          {...field}
                          id="patenteInput"
                          className={
                            `form-control text-uppercase 
                            ${ !field.value ? "border-warning bg-warning-subtle" : "border-success"} 
                            ${ fieldState.error ? 'is-invalid' : ''}`
                          }
                          placeholder="Ingrese patente"
                          list="patentesList"
                          autoComplete="off"
                          spellCheck={false}
                          onChange={(e) => {
                            const patente = e.target.value.toUpperCase();
                            field.onChange(patente);
                            prellenarDesdeVehiculo(patente);
                          }}
                        />
                        <datalist id="patentesList">
                          {patentes.map(p => (
                            <option key={p} value={p} />
                          ))}
                        </datalist>
                        {fieldState.error && (
                          <div className="invalid-feedback d-block">
                            {fieldState.error.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                  <label className="text-muted m-0">Tipo de vehículo: <strong>{ tipoVehiculo || "No seleccionado" }</strong></label>
                </div>
                {/* KILOMETRAJE */}
                <div className="form-comprobante-field mb-2">
                    <label className="form-label">
                      Kilometraje actual <span className="required">*</span>
                    </label>
                    < Controller 
                      name="kmActual"
                      control={control}
                      rules={{
                        required: "Debe ingresar el kilometraje",
                        min: { value: 0, message: "El kilometraje no puede ser negativo" },
                        max: { value: 999999, message: "El kilometraje no puede superar 999.999 km" },
                        validate: (value, formValues) => {
                          const kmBase = Number(formValues.kmBase || 0);
                          if (Number(value) <= kmBase) {
                            return `El kilometraje no puede ser menor o igual al registrado (${kmBase.toLocaleString('es-CL')} km)`;
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                      <>
                        <InputNumber
                          inputId="kmActualInput"
                          value={field.value ?? null}
                          onValueChange={(e) => field.onChange(e.value)} // e.value = número puro (100000)
                          onBlur={field.onBlur}
                          locale="es-CL" // separador de miles con punto
                          useGrouping
                          suffix=" km"
                          min={0}
                          max={999999}
                          maxLength={10} // 6 dígitos + 2 puntos + " km"
                          placeholder="0 km"
                          inputMode="numeric"
                          className="w-100 km-input"
                          inputClassName={fieldState.error ? 'p-invalid' : ''}
                        />
                        {fieldState.error && (
                          <div className="invalid-feedback d-block">
                            {fieldState.error.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
              </Section> 

              {/* Vigencia de documentos */}
              <Section title="II. VIGENCIA DE DOCUMENTOS">
                <div className="d-grid gap-1">
                  <DateDisplayRow name="vencRevisionTecnica" label="Revisión Técnica" />
                  <DateDisplayRow name="vencPermisoCirculacion" label="Permiso de Circulación" />
                  <DateDisplayRow name="vencSoap" label="SOAP" />
                  <DateDisplayRow name="vencExtintor" label="Mantenimiento Extintor" />
                  
                  {["VAN", "MINIBUS"].includes(tipoVehiculo) && (
                    <>
                      <DateDisplayRow name="vencDecreto80" label="Decreto 80" />
                      <DateDisplayRow name="vencLaminaSeguridad" label="Lámina de Seguridad" />
                    </>
                  )}
                </div>
              </Section>

              {/* Documentación y permisos */}
              <Section title="III. ¿SE ENCUENTRA EN EL VEHÍCULO?">
                <div className="mb-1">
                  <p>Verifique si la siguiente documentación y equipos de emergencia y seguridad se encuentran físicamente en el vehículo. Recuerde que es obligatorio portar estos documentos durante la conducción.</p>

                  <div className="d-flex gap-2 justify-content-end flex-wrap">
                    <button type="button" onClick={quickFillSi} className="btn btn-success btn-sm mb-2">
                      Marcar todo "Sí"
                    </button>
                  </div>

                  {/* Presencia de documentos en el vehículo */}
                  <label className="label-sub-seccion">
                    Documentación en el vehículo
                  </label>
                  <div className="d-grid gap-1 mb-3">
                    <SiNoRow name="doc_revisionTecnica"      label="Revisión Técnica" />
                    <SiNoRow name="doc_permisoCirculacion"   label="Permiso de Circulación" />
                    <SiNoRow name="doc_soap"                 label="SOAP" />
                    <SiNoRow name="doc_emisionGases"         label="Emisión de Gases" />
                    <SiNoRow name="doc_padron"               label="Padrón" />
                    {["VAN", "MINIBUS"].includes(tipoVehiculo) && (
                      <SiNoRow name="doc_laminaSeguridad" label="Certificado Lámina de Seguridad" />
                    )}
                  </div>

                  {/* Presencia de equipos de seguridad en el vehículo */}
                  <label className="label-sub-seccion">
                    Equipamiento de emergencia y seguridad
                  </label>
                  <div className="d-grid gap-1 mb-3">
                    <SiNoRow name="botiquin"    label="Botiquín" />
                    <SiNoRow name="chaleco"     label="Chaleco reflectante" />
                    <SiNoRow name="lentes"      label="Lentes de seguridad" />
                    <SiNoRow name="guantes"     label="Guantes" />
                    <SiNoRow name="bloqueador"  label="Bloqueador solar" />
                    <SiNoRow name="utilesAseo"  label="Útiles de aseo" />
                  </div>
                </div>
              </Section>

              {/* Inspección de Vehículo */}
              <Section title="IV. INSPECCIÓN DE VEHÍCULO">
                <p>Realice una inspección visual y funcional de los componentes y elementos de seguridad del vehículo. Registre el estado de cada ítem según corresponda.</p>

                <div className="d-flex gap-2 justify-content-end mb-2 flex-wrap">
                  <button type="button" onClick={quickFillBueno} className="btn btn-success btn-sm">
                    Marcar todo "Bueno"
                  </button>
                </div>

                {/* Camaras */}
                <label className="label-sub-seccion">Cámaras de seguridad</label>
                <div className="mb-3 mt-2">
                  <RadioRow name="camaraInterior" label="Cámara interior" />
                  <RadioRow name="camaraExterior" label="Cámara exterior" />
                </div>

                {/* Extintor */}
                <label className="label-sub-seccion">Extintor</label>
                <div className="mb-3 mt-2">
                  <RadioRow name="extintorPin" label="Pin de seguridad" />
                  <RadioRow name="extintorCargado" label="Cargado" />
                </div>

                {/* Herramientas */}
                <label className="label-sub-seccion">Herramientas del vehículo</label>
                <div className="mb-3 mt-2">
                  <RadioRow name="triangulos" label="Triángulos reflectantes" />
                  <RadioRow name="gata" label="Gata" />
                  <RadioRow name="llaveRuedas" label="Llave de ruedas" />
                  <RadioRow name="cunas" label="Cuñas" />
                  <RadioRow name="ganchoRemolque" label="Gancho de remolque" />
                </div>

                {/* Iluminación */}
                <label className="label-sub-seccion">Sistema de iluminación</label>
                <div className="mb-3 mt-2">
                <RadioRow name="lucesBajas" label="Luces bajas" />
                <RadioRow name="lucesAltas" label="Luces altas" />
                <RadioRow name="lucesDireccionales" label="Luces direccionales" />
                <RadioRow name="lucesEmergencia" label="Luces de emergencia" />
                <RadioRow name="lucesReversa" label="Luces de reversa" />
                { (tipoVehiculo !== "TRANSPORTADO" && modeloVehiculo !== "STARIA") && ( <RadioRow name="lucesAntiniebla" label="Luces antiniebla" /> )}
                <RadioRow name="lucesCabina" label="Luces de cabina" />
                </div>

                {/* Mecánicos y seguridad */}
                <label className="label-sub-seccion">Componentes mecánicos y de seguridad</label>
                <div className="mb-3 mt-2">
                  <RadioRow name="frenos" label="Frenos" />
                  {tipoVehiculo === "TAXI" && (<RadioRow name="alarmaRetroceso" label="Alarma de retroceso" />)}
                </div>

                {/* Carrocería */}
                <label className="label-sub-seccion">Carrocería y accesorios externos</label>
                <div className="mb-3 mt-2">
                <RadioRow name="parabrisas" label="Parabrisas" />
                <RadioRow name="limpiaparabrisas" label="Limpiaparabrisas" />
                <RadioRow name="vidriosLaterales" label="Vidrios laterales" />
                <RadioRow name="espejosLaterales" label="Espejos laterales" />
                {tipoVehiculo === "VANS" && ( <RadioRow name="limpiaparabrisasTrasero" label="Limpiaparabrisas trasero" /> )}
                {/* Oculto solo para transportado*/}
                {tipoVehiculo !== "TRANSPORTADO" && (
                  <>
                  <RadioRow name="vidrioTrasero" label="Vidrio trasero" />
                  <RadioRow name="espejoRetrovisor" label="Espejo retrovisor" />
                  </>
                )}
                </div>

                {/* Motor y fluidos */}
                <label className="label-sub-seccion">Motor y fluidos</label>
                <div className="mb-3 mt-2">
                <RadioRow name="aceiteMotor" label="Aceite de motor" />
                <RadioRow name="refrigerante" label="Refrigerante" />
                <RadioRow name="direccion" label="Dirección" />
                <RadioRow name="liquidoFrenos" label="Líquido de frenos" />
                <RadioRow name="aguaLimpiaparabrisas" label="Agua limpia parabrisas" />
                { PATENTES_ADBLUE.includes(patenteSeleccionada) && ( <RadioRow name="adBlue" label="Adblue" /> )} 
                </div>

                {/* Neumáticos - desgaste */}
                <label className="label-sub-seccion">Neumáticos - Desgaste estructural</label>
                <div className="mb-3 mt-2">
                <RadioRow name="desgasteDD" label="Delantero derecho" />
                <RadioRow name="desgasteDI" label="Delantero izquierdo" />
                <RadioRow name="desgasteTD" label="Trasero derecho" />
                <RadioRow name="desgasteTI" label="Trasero izquierdo" />
                <RadioRow name="desgasteRepuesto" label="Repuesto" />
                </div>

                {/* Neumáticos - presión */}
                <label className="label-sub-seccion">Neumáticos - Presión</label>
                <div className="mb-3 mt-2">
                <RadioRow name="presionDD" label="Delantero derecho" />
                <RadioRow name="presionDI" label="Delantero izquierdo" />
                <RadioRow name="presionTD" label="Trasero derecho" />
                <RadioRow name="presionTI" label="Trasero izquierdo" />
                <RadioRow name="presionRepuesto" label="Repuesto" />
                </div>

                {/* Interior y controles */}
                <label className="label-sub-seccion">Interior y controles</label>
                <div className="mb-3 mt-2">
                <RadioRow name="asientos" label="Estado Asientos" />
                <RadioRow name="tapiceria" label="Estado Tapicería" />
                <RadioRow name="aseoVehiculo" label="Aseo del vehículo" />
                <RadioRow name="manillasPuertas" label="Manillas de puertas" />
                <RadioRow name="segurosPuertas" label="Seguros de puertas" />
                <RadioRow name="alzavidrios" label="Alzavidrios" />
                <RadioRow name="ajustePuertas" label="Ajuste de las puertas" />
                <RadioRow name="bocina" label="Bocina" />
                { tipoVehiculo !== "TAXI" && ( <RadioRow name="logosImpresos" label="Logos impresos" /> )}
                <RadioRow name="cinturones" label="Cinturones de seguridad" />
                <RadioRow name="mandosElectricos" label="Mandos eléctricos" />
                <RadioRow name="tableroControles" label="Tablero de controles" />
                <RadioRow name="aireAcondicionado" label="Aire acondicionado" />
                <RadioRow name="frenoMano" label="Freno de mano" />
                <RadioRow name="correas" label="Correas" />
                </div>

                {/* Preguntas para transportado (vehiculo refrigerado) */}
                { tipoVehiculo === "TRANSPORTADO" && (
                  <div>
                    <label className="label-sub-seccion">Refrigeración y control de temperatura</label>
                    <div className="mb-3 mt-2">
                      <RadioRow name="ref_daniosVisibles" label="Daños visibles" />
                      <RadioRow name="ref_obstruccion" label="Obstrucción" />
                      <RadioRow name="ref_mangueras" label="Estado de mangueras" />
                      <RadioRow name="ref_ventiladores" label="Estado de ventiladores" />
                      <RadioRow name="ref_ruidos" label="Ruido en ventiladores" />
                      <RadioRow name="ref_revisionTemp" label="Revisión de temperatura" />
                      <RadioRow name="ref_limpiezaGral" label="Limpieza general" />
                      <RadioRow name="ref_sensoresTemp" label="Estado sensores de temperatura" />
                      <RadioRow name="ref_puertasSellos" label="Estado puertas y sellos" />
                      <RadioRow name="ref_cierreHermetico" label="Estado cierre hermetico" />
                      <RadioRow name="ref_tempInicial" label="Temperatura inicial" />
                    </div>
                  </div>
                )}
              </Section>

              {/* Observaciones */}
              <Section title="V. OBSERVACIONES / SÍNTOMA VEHICULAR">
                <p>Registre cualquier observación, anomalía, daño, falla o síntoma detectado durante la inspección o conducción. Esta información quedará registrada en el histórico del vehículo para su seguimiento.</p>
                <Observaciones />
              </Section>
              
              {/* Cajon de botones */}
              <div className='tmc-btns-container mt-2 justify-content-around'>
                <Button type="button" label="Limpiar" icon="pi pi-eraser" 
                  className="btn-text" onClick={() => reset()}
                />
                <Button type="button" label="Guardar" icon="pi pi-save" 
                  className="inv-btn-confirm" 
                  onClick={handleSubmit(enviarChecklist)}
                  disabled={isSubmitting}
                />
              </div>
            </form>
          </section>
        </section>
      </FormProvider>
    </ModuleLayout>
  );
}

// === Definición preguntas del checklist ===
const DEFINICION_CHECKLIST = [
  // Documentación — presencia física en el vehículo (respuesta: si/no)
  { pregunta: "doc_revisionTecnica", grupo: "Documentación", tipo: "sino" },
  { pregunta: "doc_permisoCirculacion", grupo: "Documentación", tipo: "sino" },
  { pregunta: "doc_soap", grupo: "Documentación", tipo: "sino" },
  { pregunta: "doc_emisionGases", grupo: "Documentación", tipo: "sino" },
  { pregunta: "doc_padron", grupo: "Documentación", tipo: "sino" },
  {
    pregunta: "doc_laminaSeguridad", grupo: "Documentación", tipo: "sino",
    aplica: (tipo) => ["VAN", "MINIBUS"].includes(tipo)
  },

  // Seguridad personal
  { pregunta: "botiquin", grupo: "Seguridad personal", tipo: "sino" },
  { pregunta: "chaleco", grupo: "Seguridad personal", tipo: "sino" },
  { pregunta: "lentes", grupo: "Seguridad personal", tipo: "sino" },
  { pregunta: "guantes", grupo: "Seguridad personal", tipo: "sino" },
  { pregunta: "bloqueador", grupo: "Seguridad personal", tipo: "sino" },
  { pregunta: "utilesAseo", grupo: "Seguridad personal", tipo: "sino" },

  // Camaras
  { pregunta: "camaraInterior", grupo: "Cámaras de seguridad" },
  { pregunta: "camaraExterior", grupo: "Cámaras de seguridad" },

  // Extintor
  { pregunta: "extintorPin", grupo: "Extintor" },
  { pregunta: "extintorCargado", grupo: "Extintor" },

  // Herramientas
  { pregunta: "triangulos", grupo: "Herramientas" },
  { pregunta: "gata", grupo: "Herramientas" },
  { pregunta: "llaveRuedas", grupo: "Herramientas" },
  { pregunta: "cunas", grupo: "Herramientas" },
  { pregunta: "ganchoRemolque", grupo: "Herramientas" },

  // Iluminación
  { pregunta: "lucesBajas", grupo: "Iluminación" },
  { pregunta: "lucesAltas", grupo: "Iluminación" },
  { pregunta: "lucesDireccionales", grupo: "Iluminación" },
  { pregunta: "lucesEmergencia", grupo: "Iluminación" },
  { pregunta: "lucesReversa", grupo: "Iluminación" },

  // oculto solo para TRANSPORTADO, aplica si NO es TRANSPORTADO
  { 
    pregunta: "lucesAntiniebla", grupo: "Iluminación",
    aplica: (tipo) => tipo !== "TRANSPORTADO"
  },
  { pregunta: "lucesCabina", grupo: "Iluminación" },

  // Mecánicos y seguridad
  { pregunta: "frenos", grupo: "Mecánicos y seguridad" },
  { pregunta: "alarmaRetroceso", grupo: "Mecánicos y seguridad" },

  // Carrocería
  { pregunta: "parabrisas", grupo: "Carrocería" },
  { pregunta: "limpiaparabrisas", grupo: "Carrocería" },
  { pregunta: "vidriosLaterales", grupo: "Carrocería" },

  // Solo se muestra a VANS
  { 
    pregunta: "limpiaparabrisasTrasero", grupo: "Carrocería",
    aplica: (tipo) => tipo === "VANS"
  },

  // Ocultos solo para TRANSPORTADO
  { 
    pregunta: "vidrioTrasero", grupo: "Carrocería",
    aplica: (tipo) => tipo !== "TRANSPORTADO"
  },
  { 
    pregunta: "espejoRetrovisor", grupo: "Carrocería",
    aplica: (tipo) => tipo !== "TRANSPORTADO"
  },

  { pregunta: "espejosLaterales", grupo: "Carrocería" },

  // Motor y fluidos
  { pregunta: "aceiteMotor", grupo: "Motor y fluidos" },
  { pregunta: "refrigerante", grupo: "Motor y fluidos" },
  { pregunta: "direccion", grupo: "Motor y fluidos" },
  { pregunta: "liquidoFrenos", grupo: "Motor y fluidos" },
  { pregunta: "aguaLimpiaparabrisas", grupo: "Motor y fluidos" },
  { pregunta: "adBlue", grupo: "Motor y fluidos",
    aplica: (tipo, patente) => PATENTES_ADBLUE.includes(patente),
  },

  // Neumáticos - desgaste
  { pregunta: "desgasteDD", grupo: "Neumáticos - desgaste" },
  { pregunta: "desgasteDI", grupo: "Neumáticos - desgaste" },
  { pregunta: "desgasteTD", grupo: "Neumáticos - desgaste" },
  { pregunta: "desgasteTI", grupo: "Neumáticos - desgaste" },
  { pregunta: "desgasteRepuesto", grupo: "Neumáticos - desgaste" },

  // Neumáticos - presión
  { pregunta: "presionDD", grupo: "Neumáticos - presión" },
  { pregunta: "presionDI", grupo: "Neumáticos - presión" },
  { pregunta: "presionTD", grupo: "Neumáticos - presión" },
  { pregunta: "presionTI", grupo: "Neumáticos - presión" },
  { pregunta: "presionRepuesto", grupo: "Neumáticos - presión" },

  // Interior y controles
  { pregunta: "asientos", grupo: "Interior y controles" },
  { pregunta: "tapiceria", grupo: "Interior y controles" },
  { pregunta: "aseoVehiculo", grupo: "Interior y controles" },
  { pregunta: "manillasPuertas", grupo: "Interior y controles" },
  { pregunta: "segurosPuertas", grupo: "Interior y controles" },
  { pregunta: "alzavidrios", grupo: "Interior y controles" },
  { pregunta: "ajustePuertas", grupo: "Interior y controles" },
  { pregunta: "bocina", grupo: "Interior y controles" },

  // Oculto solo para TAXI
  { 
    pregunta: "logosImpresos", 
    grupo: "Interior y controles",
    aplica: (tipo) => tipo !== "TAXI"
  },

  { pregunta: "cinturones", grupo: "Interior y controles" },
  { pregunta: "mandosElectricos", grupo: "Interior y controles" },
  { pregunta: "tableroControles", grupo: "Interior y controles" },
  { pregunta: "aireAcondicionado", grupo: "Interior y controles" },
  { pregunta: "frenoMano", grupo: "Interior y controles" },
  { pregunta: "correas", grupo: "Interior y controles" },

  // Transportado: solo para tipoVehiculo === "TRANSPORTADO"
  { 
    pregunta: "ref_daniosVisibles",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_obstruccion",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_mangueras",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_ventiladores",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_ruidos",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_revisionTemp",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_limpiezaGral",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_sensoresTemp",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_puertasSellos",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_cierreHermetico",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
  { 
    pregunta: "ref_tempInicial",
    grupo: "Refrigeración y control de temperatura",
    aplica: (tipo) => tipo === "TRANSPORTADO"
  },
];

const OPCIONES_ESTADOS = [
  { label: "Bueno", value: "bueno"},
  { label: "Revisar", value: "revisar"},
  { label: "Falta", value: "falta"},
];

// === Campos ===
const Section = ({ title, children }) => (
  <div className="rounded-3 p-3 border shadow-sm mb-2">
    <label className="section-title">{title}</label>
    <div className="gap-2">{children}</div>
  </div>
);

const RadioRow = ({ name, label }) => {
  const { control } = useFormContext();

  // Controlamos radio buton con RHF, usando regla required
  const { field, fieldState } = useController({
    name, control, rules: { required: "Requerido" }, defaultValue: ""
  });

  return (
    <>
      <div className="radio-row mb-0 justify-content-between gap-1 flex-wrap">
        
        {/* Pregunta */}
        <div className="sino-question">
          <label className="form-label mb-0">
            {label}<span className="required">*</span>
          </label>
        </div>

        {/* Botones Bueno / Revisar / Falta */}
        <div className="sino-options mb-2">
          {OPCIONES_ESTADOS.map((opt) => (
            <label 
              key={opt.value} 
              className="radio-row mb-0 align-items-center"
            > 
              <RadioButton
                inputId={`${name}-${opt.value}`}
                value={opt.value}
                onChange={(e) => field.onChange(e.value)}
                checked={field.value === opt.value}
                className={fieldState.error ? "p-invalid" : ""}
              />
              <span>{opt.label}</span>
            </label>
          ))}

          {fieldState.error && (
            <div className="invalid-feedback d-block">
              {fieldState.error.message}
            </div>
          )}
        </div>
      </div>
      <hr className="m-1"/>
    </>
  );
};

const InputRow = ({ name, label, type = "text", placeholder, disabled, rules }) => {
  const { register, formState } = useFormContext();
  const error = formState.errors?.[name];
  
  return (
    <div className="mb-0 d-flex align-items-center flex-wrap">
      <label className="form-label">
        {label}<span className="required">*</span>
      </label>

      <input 
        className={`form-control ${error ? 'is-invalid' : ''}`}
        style={{ minWidth: 100 }} 
        {...register(name, rules)}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        min={type === "number" ? 0 : undefined}
        step={type === "number" ? "1" : undefined}
      />

      {error && <div className="invalid-feedback">{error.message}</div>}
    </div>
  );
};

// Calcula el estado de vencimiento de una fecha ISO "YYYY-MM-DD"
// Retorna: "vencido" | "por_vencer" | "vigente" | "sin_fecha"
const calcularEstadoFecha = (fechaStr) => {
  if (!fechaStr) return "sin_fecha";
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const venc = new Date(fechaStr + "T00:00:00");
  const diffMs = venc - hoy;
  const diffDias = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDias < 0) return "vencido";
  if (diffDias <= 30) return "por_vencer";
  return "vigente";
};

const ESTADO_FECHA_STYLES = {
  vencido:    { bg: "#fff0f0", border: "#f5c6cb", badge: "#dc3545", text: "Vencido",        icon: "pi pi-times-circle" },
  por_vencer: { bg: "#fffbe6", border: "#ffd666", badge: "#e67e22", text: "Por vencer",     icon: "pi pi-exclamation-triangle" },
  vigente:    { bg: "#f0fff4", border: "#b7ebc9", badge: "#198754", text: "Vigente",         icon: "pi pi-check-circle" },
  sin_fecha:  { bg: "#f8f9fa", border: "#dee2e6", badge: "#78818a", text: "Sin registro", icon: "pi pi-calendar-times" },
};

const DateDisplayRow = ({ name, label }) => {
  const { watch } = useFormContext();
  const fechaStr = watch(name);
  const estado = calcularEstadoFecha(fechaStr);

  const claseEstado = {
    vencido: "fecha-documento--vencido",
    por_vencer: "fecha-documento--por-vencer",
    vigente: "fecha-documento--vigente",
    sin_fecha: "fecha-documento--sin-fecha",
  };

  return (
    <div className={`fecha-documento ${claseEstado[estado]}`}>
      {label}

      <div className="fecha-documento__contenido">
        {fechaStr && (
          <span className="fecha-documento__valor">
            {formatFecha(fechaStr)}
          </span>
        )}

        <span className="fecha-documento__estado">
          <i
            className={`${ESTADO_FECHA_STYLES[estado].icon} fecha-documento__icono`}
          />
          {ESTADO_FECHA_STYLES[estado].text}
        </span>
      </div>
    </div>
  );
};

// Fila con opciones Sí / No (para Documentación y Seguridad Personal)
const SiNoRow = ({ name, label }) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name, control, rules: { required: "Requerido" }, defaultValue: ""
  });

  const OPCIONES_SI_NO = [
    { label: "Sí", value: "si" },
    { label: "No", value: "no" },
  ];

  return (
    <>
      <div className="radio-row">
        <div className="sino-question">
          <label className="form-label mb-0">
            {label}<span className="required">*</span>
          </label>
        </div>
        <div className="sino-options">
          {OPCIONES_SI_NO.map((opt) => (
            <label key={opt.value} className="radio-row mb-0 align-items-center">
              <RadioButton
                inputId={`${name}-${opt.value}`}
                value={opt.value}
                onChange={(e) => field.onChange(e.value)}
                checked={field.value === opt.value}
                className={fieldState.error ? "p-invalid" : ""}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          {fieldState.error && (
            <div className="invalid-feedback d-block">
              {fieldState.error.message}
            </div>
          )}
        </div>
      </div>
      <hr className="m-0"/>
    </>
  );
};

const Observaciones = () => {
  const { register } = useFormContext();
  return (
    <div className="d-grid gap-2">
      <textarea 
        className="form-control" rows={4} 
        placeholder="Ingrese aquí cualquier síntoma u observación general sobre el estado del vehículo." 
        {...register("observaciones")}
      />
    </div>
  );
};